import React, { FC } from 'react';

import {
  CreateFavoriteFieldOperationMutation as CreateFavoriteFieldOperation,
  CreateFavoriteFieldOperationMutationVariables as CreateFavoriteFieldOperationVariables,
  DeleteFavoriteFieldOperationMutation as DeleteFavoriteFieldOperation,
  DeleteFavoriteFieldOperationMutationVariables as DeleteFavoriteFieldOperationVariables,
  FavoriteFieldsQueryOperationQuery as FavoriteFieldsQueryOperation,
  FavoriteFieldType,
} from 'core/graphql';
import { useMutation, useQuery } from 'shared/hooks';

import { FavoriteField } from './FavoriteField';
import {
  CREATE_FAVORITE_FIELDS_MUTATION,
  DELETE_FAVORITE_FIELDS_MUTATION,
} from './mutations';
import { getApplicableFavoriteFields } from './parsers';
import { FAVORITE_FIELDS_QUERY } from './queries';

interface FavoriteFieldContainerProps {
  disabled?: boolean;
  fieldName: string;
  fieldType: FavoriteFieldType;
  objectType?: string;
  showIcon?: boolean;
}

export const FavoriteFieldContainer: FC<
  React.PropsWithChildren<FavoriteFieldContainerProps>
> = ({ children, disabled, fieldName, fieldType, objectType, showIcon }) => {
  // Only skip the query when `showIcon` is false. Still execute the query when
  // its value is undefined or true.
  const skip = showIcon === false;
  const { data } = useQuery<FavoriteFieldsQueryOperation>(
    FAVORITE_FIELDS_QUERY,
    { skip }
  );

  // Determine which fields are favorite for the given object type.
  const favoriteFields = data?.favoriteFields.objects;
  const applicableFavoriteFields = favoriteFields
    ? getApplicableFavoriteFields(favoriteFields, fieldType, objectType)
    : [];

  const favoriteMatch = applicableFavoriteFields.find(
    (item) => item.fieldName === fieldName
  );

  const [createFavoriteField, { loading: createLoading }] = useMutation<
    CreateFavoriteFieldOperation,
    CreateFavoriteFieldOperationVariables
  >(CREATE_FAVORITE_FIELDS_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: FAVORITE_FIELDS_QUERY }],
    variables: { fieldName, fieldType },
  });

  const id = favoriteMatch?.id || '';
  const [deleteFavoriteField, { loading: deleteLoading }] = useMutation<
    DeleteFavoriteFieldOperation,
    DeleteFavoriteFieldOperationVariables
  >(DELETE_FAVORITE_FIELDS_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: FAVORITE_FIELDS_QUERY }],
    variables: { id },
  });

  const toggleFn = favoriteMatch ? deleteFavoriteField : createFavoriteField;

  const iconVisible =
    !deleteLoading && (createLoading || showIcon || !!favoriteMatch);

  return (
    <FavoriteField
      disabled={disabled || createLoading || (!!favoriteMatch && !id)}
      onClick={() => toggleFn()}
      showIcon={iconVisible}
    >
      {children}
    </FavoriteField>
  );
};
